import { createContext, useContext, ReactNode, useMemo, ReactElement, useState } from 'react';

type SelectData = {
  title: string;
  description?: string;
  thumbnailUrl?: string;
};

type ArticleModalContextValue = {
  onSelect?: (data: SelectData) => void;
  currentSelection: SelectData | null;
  submitSelection: (data: SelectData) => void;
};

const ArticleModalContext = createContext<ArticleModalContextValue | undefined>(undefined);

export const ArticleModalProvider = ({
  children,
  submitSelection,
}: {
  children: ReactNode;
  submitSelection: (data: SelectData) => void;
}): ReactElement => {
  const [currentSelection, setCurrentSelection] = useState<SelectData | null>(null);

  const handleSelect = (data: SelectData): void => {
    setCurrentSelection(data);
  };

  const value = useMemo(
    () => ({
      onSelect: handleSelect,
      currentSelection,
      submitSelection: (data: SelectData) => {
        if (submitSelection) {
          submitSelection(data);
        }
      },
    }),
    [currentSelection, submitSelection]
  );

  if (!submitSelection) {
    throw new Error('submitSelection function is required in ArticleModalProvider');
  }

  return <ArticleModalContext.Provider value={value}>{children}</ArticleModalContext.Provider>;
};

export const useArticleModalContext = (): ArticleModalContextValue => {
  const context = useContext(ArticleModalContext);
  if (!context)
    throw new Error('useArticleModalContext must be used within an ArticleModalProvider');
  return context;
};
